export type GoogleAddressComponent = {
  types: string[];
  longText?: string;
  shortText?: string;
};

export type SelectedGoogleAddress = {
  streetAddress: string;
  unit: string;
  city: string;
  province: string;
  provinceCode: string;
  postalCode: string;
  country: string;
  countryCode: string;
};

export const mapGoogleAddress = (components?: GoogleAddressComponent[]): SelectedGoogleAddress | null => {
  const value = (type: string, short = false) => {
    const component = components?.find(item => item.types.includes(type));
    return ((short ? component?.shortText : component?.longText) || '').trim();
  };
  const streetAddress = value('street_address') || [value('street_number'), value('route')].filter(Boolean).join(' ');
  if (!streetAddress) return null;
  const postalCode = value('postal_code');
  const suffix = value('postal_code_suffix');
  return {
    streetAddress, unit: value('subpremise'),
    city: value('locality') || value('postal_town') || value('sublocality_level_1'),
    province: value('administrative_area_level_1'), provinceCode: value('administrative_area_level_1', true),
    postalCode: postalCode && suffix ? `${postalCode}-${suffix}` : postalCode,
    country: value('country'), countryCode: value('country', true),
  };
};

export const googleAddressToMemberFields = (address: SelectedGoogleAddress) => ({
  streetAddress: [address.streetAddress, address.unit ? `Unit ${address.unit}` : ''].filter(Boolean).join(', '),
  city: address.city, provinceOrState: address.province || address.provinceCode,
  postalOrZipCode: address.postalCode, country: address.country,
});

// Invoice billing address parts (Update Invoice InvoicePartyFields and the
// Create Advertising/Custom Invoice address).
export const googleAddressToInvoiceAddress = (address: SelectedGoogleAddress) => ({
  addressLine1: address.streetAddress,
  addressLine2: address.unit ? `Unit ${address.unit}` : '',
  city: address.city,
  province: address.province || address.provinceCode,
  postalCode: address.postalCode,
  country: address.country,
});

export type GooglePlacePrediction = {
  placeId: string;
  text: { toString(): string };
  toPlace(): {
    addressComponents?: GoogleAddressComponent[];
    fetchFields(input: { fields: string[] }): Promise<unknown>;
  };
};

export type GooglePlacesLibrary = {
  AutocompleteSessionToken: new () => object;
  AutocompleteSuggestion: {
    fetchAutocompleteSuggestions(input: { input: string; sessionToken: object; includedRegionCodes?: string[] }): Promise<{
      suggestions: Array<{ placePrediction?: GooglePlacePrediction }>;
    }>;
  };
};

type MapsWindow = Window & {
  google?: { maps?: { importLibrary?: (name: string) => Promise<GooglePlacesLibrary> } };
  __cmpAddressMapsReady?: () => void;
};
let placesPromise: Promise<GooglePlacesLibrary> | undefined;
const browserKey = () => (process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '').trim();

export const isGoogleAddressAutocompleteConfigured = () => Boolean(browserKey());

// The browser key must have HTTP-referrer and Maps JavaScript/Places API restrictions.
// See https://developers.google.com/maps/documentation/javascript/place-autocomplete-data.
// Moeen (Oct 16 2026): suggestions only from Canada and the United States, the two
// countries the address forms accept.
export const GOOGLE_ADDRESS_REGION_CODES = ['ca', 'us']

export const loadGoogleAddressPlaces = (): Promise<GooglePlacesLibrary> => {
  if (placesPromise) return placesPromise;
  const browser = window as MapsWindow;
  const key = browserKey();
  if (!key) return Promise.reject(new Error('Address search is unavailable.'));
  placesPromise = (async () => {
    if (!browser.google?.maps?.importLibrary) {
      await new Promise<void>((resolve, reject) => {
        const existing = document.querySelector<HTMLScriptElement>('script[src^="https://maps.googleapis.com/maps/api/js"]');
        const script = existing || document.createElement('script');
        const finish = (error?: Error) => {
          clearTimeout(timer);
          script.removeEventListener('load', onLoad);
          script.removeEventListener('error', onError);
          if (!existing) delete browser.__cmpAddressMapsReady;
          if (error) { if (!existing) script.remove(); reject(error); }
          else resolve();
        };
        const onLoad = () => browser.google?.maps?.importLibrary
          ? finish() : finish(new Error('Address search is unavailable.'));
        const onError = () => finish(new Error('Address search is unavailable.'));
        const timer = window.setTimeout(onError, 10000);
        script.addEventListener('error', onError, { once: true });
        if (existing) script.addEventListener('load', onLoad, { once: true });
        else {
          browser.__cmpAddressMapsReady = onLoad;
          script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&loading=async&libraries=places&callback=__cmpAddressMapsReady`;
          script.async = true;
          document.head.appendChild(script);
        }
      });
    }
    const library = await browser.google?.maps?.importLibrary?.('places');
    if (!library?.AutocompleteSuggestion || !library.AutocompleteSessionToken) throw new Error('Address search is unavailable.');
    return library;
  })().catch(error => { placesPromise = undefined; throw error; });
  return placesPromise;
};
